/**
 * useLibraryUploadQueue — sends a bulk upload a few files at a time and
 * keeps the server's record of it.
 *
 * Before this, every picked file was POSTed at once and the library was
 * re-read after each one: fine for ten files, a stampede for 3,000. Now:
 *   - files wait in a queue and at most ``CONCURRENCY`` are in flight;
 *   - every file of the action is named to the server first
 *     (``registerUploadBatch``), so its record knows what was meant to go in
 *     even if this pane closes half way; the upload route writes each
 *     outcome there, and what only this pane sees (a duplicate held back, a
 *     request that never reached the server) is reported here;
 *   - ``onUploaded`` fires at most every ``REFRESH_EVERY_MS`` while files go
 *     in, and once more when the queue drains, so the caller can re-read the
 *     library without one read per file;
 *   - failed files keep their ``File`` so "Retry failed" can send them again.
 *
 * The record is best-effort: if registering fails the files are sent anyway,
 * without a batch, exactly as before.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { ClientUploadState, UploadBatchRef, UploadManifestFile } from "../../../api/client";
import { UploadError } from "../../../api/client";
import { partitionUploads } from "../dedupeUploads";
import { relativePathOf } from "../folderPaths";
import type { UploadSlot } from "../types";

/** Files in flight at once. */
export const CONCURRENCY = 3;
/** Re-read the library at most this often while files are going in. */
export const REFRESH_EVERY_MS = 45_000;

export interface UploadQueueDeps {
  upload: (libraryId: string, file: File, batch?: UploadBatchRef) => Promise<unknown>;
  /** Names of the documents already in the library, for the duplicate check. */
  libraryNames: (libraryId: string) => Promise<string[]>;
  /** Optional server record of the upload; without it files are sent plainly. */
  registerBatch?: (libraryId: string, batchId: string, files: UploadManifestFile[]) => Promise<void>;
  reportOutcome?: (
    libraryId: string,
    batchId: string,
    fileKey: string,
    outcome: { name: string; state: ClientUploadState; message?: string | null },
  ) => Promise<void>;
  newId: () => string;
  now: () => number;
}

export interface UseLibraryUploadQueueOptions {
  deps: UploadQueueDeps;
  /** Files go in (throttled), and once when the queue drains. */
  onUploaded?: (libraryId: string) => void;
  /**
   * Names held back because the library already has a document by that name,
   * with the files themselves and their library so the writer can still
   * replace the existing documents with them.
   */
  onDuplicates?: (names: string[], files: File[], libraryId: string) => void;
}

interface Queued {
  file: File;
  libraryId: string;
  batch: UploadBatchRef | null;
}

export interface LibraryUploadQueue {
  slots: UploadSlot[];
  addFiles: (libraryId: string, files: File[]) => Promise<void>;
  /** Send this library's failed files again — all of them, or the one named. */
  retryFailed: (libraryId: string, clientId?: string) => void;
  /** Drop a failed file from the queue and the list. */
  removeFailed: (clientId: string) => void;
  /** Drop this library's finished slots for a document (deleted, or replaced). */
  forget: (libraryId: string, doc: { docId?: string | null; filename?: string | null }) => void;
  /** Files still waiting or sending, across every library. */
  unfinished: number;
}

export function useLibraryUploadQueue({
  deps,
  onUploaded,
  onDuplicates,
}: UseLibraryUploadQueueOptions): LibraryUploadQueue {
  const [slots, setSlots] = useState<UploadSlot[]>([]);
  const slotsRef = useRef<UploadSlot[]>([]);
  slotsRef.current = slots;
  const filesRef = useRef(new Map<string, Queued>());
  const queueRef = useRef<string[]>([]);
  const activeRef = useRef(0);
  const lastRefreshRef = useRef(0);
  const pendingRefreshRef = useRef(new Set<string>());
  const depsRef = useRef(deps);
  depsRef.current = deps;
  const onUploadedRef = useRef(onUploaded);
  onUploadedRef.current = onUploaded;

  const patch = useCallback((clientId: string, change: Partial<UploadSlot>) => {
    setSlots((prev) => prev.map((s) => (s.client_id === clientId ? { ...s, ...change } : s)));
  }, []);

  const report = useCallback((queued: Queued, clientId: string, state: ClientUploadState, message?: string) => {
    const reportOutcome = depsRef.current.reportOutcome;
    if (!queued.batch || !reportOutcome) return;
    void reportOutcome(queued.libraryId, queued.batch.batchId, clientId, {
      name: queued.file.name,
      state,
      message,
    }).catch(() => undefined);
  }, []);

  const refresh = useCallback((libraryId: string, force: boolean) => {
    pendingRefreshRef.current.add(libraryId);
    const now = depsRef.current.now();
    if (!force && now - lastRefreshRef.current < REFRESH_EVERY_MS) return;
    lastRefreshRef.current = now;
    const libraries = [...pendingRefreshRef.current];
    pendingRefreshRef.current.clear();
    for (const id of libraries) onUploadedRef.current?.(id);
  }, []);

  const pump = useCallback(() => {
    while (activeRef.current < CONCURRENCY && queueRef.current.length > 0) {
      const clientId = queueRef.current.shift() as string;
      const queued = filesRef.current.get(clientId);
      if (!queued) continue;
      activeRef.current++;
      patch(clientId, { status: "uploading", error_message: undefined });
      depsRef.current
        .upload(queued.libraryId, queued.file, queued.batch ?? undefined)
        .then((result) => {
          filesRef.current.delete(clientId);
          // The stored doc's id pairs this slot with the library's row even when
          // the names differ, and lets a delete or re-upload retire it.
          const docId = (result as { doc_id?: unknown } | null | undefined)?.doc_id;
          patch(clientId, {
            status: "uploaded",
            upload_progress_percent: 100,
            doc_id: typeof docId === "string" && docId ? docId : null,
          });
          refresh(queued.libraryId, false);
        })
        .catch((error: unknown) => {
          const message = error instanceof Error ? error.message : "Upload failed";
          patch(clientId, { status: "failed_upload", error_message: message });
          if (!(error instanceof UploadError && error.recorded)) report(queued, clientId, "failed", message);
        })
        .finally(() => {
          activeRef.current--;
          if (activeRef.current === 0 && queueRef.current.length === 0) {
            if (pendingRefreshRef.current.size > 0) refresh(queued.libraryId, true);
          }
          pump();
        });
    }
  }, [patch, refresh, report]);

  const addFiles = useCallback(
    async (libraryId: string, files: File[]) => {
      if (files.length === 0) return;
      const d = depsRef.current;

      // Live duplicate check against the library's latest documents.
      // Fail-open on a transient list error rather than blocking uploads.
      let libraryNames: string[] = [];
      try {
        libraryNames = await d.libraryNames(libraryId);
      } catch (error) {
        console.warn("[hub] duplicate check: listing documents failed, proceeding without it", error);
      }
      const pendingNames = slotsRef.current
        .filter((s) => s.library_id === libraryId && (s.status === "waiting" || s.status === "uploading"))
        .map((s) => s.filename);
      const { allowed, blocked, skipped } = partitionUploads(files, { libraryNames, pendingNames });
      if (blocked.length > 0) onDuplicates?.(blocked.map((f) => f.name), blocked, libraryId);

      const batchId = `addin-${d.newId()}`;
      const ids = allowed.map(() => d.newId());
      const manifest: UploadManifestFile[] = [
        ...allowed.map((file, i) => manifestFile(ids[i], file, "waiting")),
        ...blocked.map((file, i) => manifestFile(`${batchId}-dup-${i}`, file, "duplicate", "Already in the library.")),
        ...skipped.map((file, i) =>
          manifestFile(`${batchId}-same-${i}`, file, "skipped", "Same name as another file in this upload."),
        ),
      ];
      let registered = false;
      if (d.registerBatch) {
        try {
          await d.registerBatch(libraryId, batchId, manifest);
          registered = true;
        } catch (error) {
          console.warn("[hub] upload record: registering the batch failed, sending without it", error);
        }
      }
      if (allowed.length === 0) return;

      const newSlots: UploadSlot[] = allowed.map((file, i) => ({
        client_id: ids[i],
        filename: file.name,
        status: "waiting",
        upload_progress_percent: null,
        doc_id: null,
        library_id: libraryId,
        ...(registered ? { batch_id: batchId } : {}),
      }));
      allowed.forEach((file, i) => {
        filesRef.current.set(ids[i], {
          file,
          libraryId,
          batch: registered ? { batchId, fileKey: ids[i] } : null,
        });
      });
      setSlots((prev) => [...prev, ...newSlots]);
      queueRef.current.push(...ids);
      pump();
    },
    [onDuplicates, pump],
  );

  const retryFailed = useCallback(
    (libraryId: string, only?: string) => {
      const again = slotsRef.current.filter(
        (s) =>
          s.library_id === libraryId &&
          s.status === "failed_upload" &&
          (only === undefined || s.client_id === only) &&
          filesRef.current.has(s.client_id),
      );
      if (again.length === 0) return;
      const ids = new Set(again.map((s) => s.client_id));
      for (const id of ids) {
        const queued = filesRef.current.get(id);
        if (queued) report(queued, id, "waiting");
      }
      setSlots((prev) =>
        prev.map((s) => (ids.has(s.client_id) ? { ...s, status: "waiting", error_message: undefined } : s)),
      );
      queueRef.current.push(...ids);
      pump();
    },
    [pump, report],
  );

  /** Take a failed file out: it leaves the list, and the record calls it taken out. */
  const removeFailed = useCallback(
    (clientId: string) => {
      const slot = slotsRef.current.find((s) => s.client_id === clientId);
      if (!slot || slot.status !== "failed_upload") return;
      const queued = filesRef.current.get(clientId);
      if (queued) report(queued, clientId, "skipped", "Taken out after it failed.");
      filesRef.current.delete(clientId);
      setSlots((prev) => prev.filter((s) => s.client_id !== clientId));
    },
    [report],
  );

  /** Finished slots only: a file still waiting or sending keeps its slot. */
  const forget = useCallback((libraryId: string, doc: { docId?: string | null; filename?: string | null }) => {
    const matches = (s: UploadSlot) =>
      s.library_id === libraryId &&
      (s.status === "uploaded" || s.status === "failed_upload") &&
      ((!!doc.docId && s.doc_id === doc.docId) || (!!doc.filename && s.filename === doc.filename));
    if (!slotsRef.current.some(matches)) return;
    for (const s of slotsRef.current) if (matches(s)) filesRef.current.delete(s.client_id);
    setSlots((prev) => prev.filter((s) => !matches(s)));
  }, []);

  const unfinished = slots.reduce((n, s) => n + (s.status === "waiting" || s.status === "uploading" ? 1 : 0), 0);

  // Closing the task pane mid-upload stops the queue; say so first.
  useEffect(() => {
    if (unfinished === 0) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unfinished]);

  return { slots, addFiles, retryFailed, removeFailed, forget, unfinished };
}

function manifestFile(key: string, file: File, state: ClientUploadState, message?: string): UploadManifestFile {
  const relativePath = relativePathOf(file);
  return {
    key,
    name: file.name,
    ...(relativePath.includes("/") ? { relative_path: relativePath } : {}),
    size_bytes: file.size,
    state,
    ...(message ? { message } : {}),
  };
}
